import { useState, useEffect, useCallback } from "react";
import {
  Send, RefreshCw, Search, CheckCircle, Clock, XCircle,
  Archive, FileText, ExternalLink, ChevronDown, Filter,
  ArrowUpDown, Users, AlertTriangle, Banknote, Inbox, Truck
} from "lucide-react";
import { toast } from "sonner";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl() + "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

const STATUT_CONFIG = {
  new: { label: "Nouveau", color: "#3b82f6", text: "text-blue-300", border: "border-blue-500/30", icon: Send },
  inprog: { label: "En cours", color: "#f59e0b", text: "text-amber-300", border: "border-amber-500/30", icon: Clock },
  pending_docs: { label: "Attente Docs", color: "#f97316", text: "text-orange-300", border: "border-orange-500/30", icon: FileText },
  sent_caisse: { label: "Envoye Caisse", color: "#6366f1", text: "text-indigo-300", border: "border-indigo-500/30", icon: Truck },
  done: { label: "Cloture", color: "#10b981", text: "text-emerald-300", border: "border-emerald-500/30", icon: CheckCircle },
  reject: { label: "Refuse", color: "#ef4444", text: "text-red-300", border: "border-red-500/30", icon: XCircle },
  paid: { label: "Paye", color: "#059669", text: "text-green-300", border: "border-green-500/30", icon: Banknote },
};

function StatutBadge({ statut }) {
  const cfg = STATUT_CONFIG[statut] || STATUT_CONFIG.new;
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`statut-badge-${statut}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.border} border ${cfg.text} bg-opacity-10`}
      style={{ backgroundColor: `${cfg.color}15` }}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function AidesCompDashboard({ onBack, authUser }) {
  const [clients, setClients] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [filterStatut, setFilterStatut] = useState("all");
  const [sortField, setSortField] = useState("nom");
  const [sortDir, setSortDir] = useState("asc");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientsRes, dossiersRes] = await Promise.all([
        authFetch(`${API}/socialac/clients`).then(r => r.json()),
        authFetch(`${API}/socialac/dossiers`).then(r => r.json()),
      ]);
      setClients(clientsRes.clients || []);
      setDossiers(dossiersRes.dossiers || []);
    } catch (e) {
      console.error("Erreur chargement:", e);
      toast.error("Erreur de chargement des donnees");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const dossierMap = {};
  dossiers.forEach(d => { dossierMap[d.row_number] = d; });

  const handleSend = async (rowNumber) => {
    setSending(rowNumber);
    try {
      const res = await authFetch(`${API}/socialac/send/${rowNumber}`, { method: "POST" });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Dossier envoye a SocialAC`);
        await loadData();
      } else {
        toast.error(data.detail || "Erreur d'envoi");
      }
    } catch (e) {
      toast.error("Erreur de connexion");
    }
    setSending(null);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await authFetch(`${API}/socialac/sync`, { method: "POST" });
      const data = await res.json();
      toast.success(`Synchro terminee: ${data.updated || 0} mis a jour`);
      await loadData();
    } catch (e) {
      toast.error("Erreur de synchronisation");
    }
    setSyncing(false);
  };

  // Build merged list
  const mergedClients = clients.map(c => {
    const rn = c.row_number;
    const dossier = dossierMap[rn];
    return {
      ...c,
      socialac_statut: dossier?.statut || null,
      socialac_id: dossier?.socialac_id || null,
      socialac_sent_at: dossier?.sent_at || null,
      socialac_etape: dossier?.etape ?? null,
    };
  });

  // Filter
  const filtered = mergedClients.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || 
      (c.nom || "").toLowerCase().includes(q) || 
      (c.prenom || "").toLowerCase().includes(q) ||
      (c.telephone || "").includes(q) ||
      (c.ville || "").toLowerCase().includes(q);
    
    const matchStatut = filterStatut === "all" || 
      (filterStatut === "non_envoye" && !c.socialac_statut) ||
      (filterStatut !== "non_envoye" && c.socialac_statut === filterStatut);

    return matchSearch && matchStatut;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortField] || "";
    let vb = b[sortField] || "";
    if (typeof va === "string") va = va.toLowerCase();
    if (typeof vb === "string") vb = vb.toLowerCase();
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  // Stats
  const totalSent = dossiers.length;
  const byStatut = {};
  dossiers.forEach(d => { byStatut[d.statut] = (byStatut[d.statut] || 0) + 1; });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-pink-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="aides-comp-dashboard">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-950/50 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-slate-400 hover:text-white transition-colors text-sm"
              data-testid="aides-comp-back-btn"
            >
              ← Retour
            </button>
            <div>
              <h1 className="text-lg font-bold text-white">Aides Complementaires</h1>
              <p className="text-xs text-slate-400">Plateforme SocialAC — {totalSent} dossier{totalSent !== 1 ? "s" : ""} envoye{totalSent !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 hover:text-white hover:border-slate-600 transition-colors disabled:opacity-50"
              data-testid="socialac-sync-btn"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Synchro..." : "Sync Statuts"}
            </button>
            <a
              href={getApiUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-pink-600/20 border border-pink-500/30 rounded-lg text-sm text-pink-300 hover:bg-pink-600/30 transition-colors"
              data-testid="socialac-external-link"
            >
              <ExternalLink className="w-4 h-4" />
              SocialAC
            </a>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-white" data-testid="stat-total-clients">{clients.length}</p>
            <p className="text-[11px] text-slate-400 uppercase tracking-wider mt-1">Total Clients</p>
          </div>
          <div className="bg-slate-800/60 border border-pink-500/20 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-pink-400" data-testid="stat-total-sent">{totalSent}</p>
            <p className="text-[11px] text-pink-400/70 uppercase tracking-wider mt-1">Envoyes</p>
          </div>
          {Object.entries(STATUT_CONFIG).map(([key, cfg]) => (
            <div key={key} className={`bg-slate-800/60 border ${cfg.border} rounded-xl p-3 text-center`}>
              <p className={`text-2xl font-bold ${cfg.text}`}>{byStatut[key] || 0}</p>
              <p className={`text-[11px] ${cfg.text} opacity-70 uppercase tracking-wider mt-1`}>{cfg.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher par nom, prenom, telephone, ville..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-pink-500/50"
              data-testid="aides-comp-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <select
              value={filterStatut}
              onChange={e => setFilterStatut(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-pink-500/50"
              data-testid="aides-comp-filter-statut"
            >
              <option value="all">Tous les statuts</option>
              <option value="non_envoye">Non envoyes</option>
              <option value="new">Nouveaux</option>
              <option value="inprog">En cours</option>
              <option value="pending_docs">Attente Docs</option>
              <option value="sent_caisse">Envoye Caisse</option>
              <option value="done">Clotures</option>
              <option value="reject">Refuses</option>
              <option value="paid">Payes</option>
            </select>
          </div>
          <span className="text-xs text-slate-500">{filtered.length} resultat{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Table */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="aides-comp-table">
              <thead>
                <tr className="border-b border-slate-700/50">
                  {[
                    { key: "nom", label: "Client" },
                    { key: "telephone", label: "Telephone" },
                    { key: "ville", label: "Ville" },
                    { key: "code_postal", label: "CP" },
                  ].map(col => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Statut SocialAC</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Envoye le</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Aucun client trouve</p>
                    </td>
                  </tr>
                ) : sorted.map(c => (
                  <tr
                    key={c.row_number}
                    className="hover:bg-slate-700/20 transition-colors"
                    data-testid={`client-row-${c.row_number}`}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-white">{c.nom} {c.prenom}</p>
                        <p className="text-xs text-slate-500">{c.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">{c.telephone || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-300">{c.ville || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-400">{(c.code_postal || "").replace("\u202f", "")}</td>
                    <td className="px-4 py-3">
                      {c.socialac_statut ? (
                        <StatutBadge statut={c.socialac_statut} />
                      ) : (
                        <span className="text-xs text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {c.socialac_sent_at
                        ? new Date(c.socialac_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {c.socialac_statut ? (
                        <span className="text-xs text-slate-500 italic">Deja envoye</span>
                      ) : (
                        <button
                          onClick={() => handleSend(c.row_number)}
                          disabled={sending === c.row_number}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-pink-600 hover:bg-pink-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                          data-testid={`send-btn-${c.row_number}`}
                        >
                          {sending === c.row_number ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <Send className="w-3 h-3" />
                          )}
                          Envoyer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
